import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "mb-page" };
const _hoisted_2 = { class: "mb-page-header" };
const _hoisted_3 = { class: "conn-indicator" };
const _hoisted_4 = { class: "alert-extra" };
const _hoisted_5 = { class: "port-row" };
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Link, SwitchButton, Search } from '@element-plus/icons-vue';
import DataLogPanel from '@/components/DataLogPanel.vue';
import RegisterViewer from '@/components/RegisterViewer.vue';
import StatusPanel from '@/components/StatusPanel.vue';
import RequestForm from '@/components/RequestForm.vue';
import { useModbusChannel } from '@/composables/useModbusChannel';
import { useRequestForm } from '@/composables/useRequestForm';
import { SerialTransport, checkSerialSupport, describePort } from '@/core/transport/SerialTransport';
import { hexToBytes } from '@/core/modbus/format';
export default /*@__PURE__*/ _defineComponent({
    __name: 'RTU',
    setup(__props) {
        const BAUD_RATES = [
            1200, 2400, 4800, 9600, 14400, 19200, 38400, 57600, 115200, 128000, 230400, 256000, 460800,
            921600
        ];
        const support = ref(checkSerialSupport());
        const serial = reactive({
            baudRate: 9600,
            dataBits: 8,
            stopBits: 1,
            parity: 'none',
            flowControl: 'none'
        });
        const selectedPort = ref(null);
        const portName = ref('');
        const { connected, connecting, connect, disconnect, execute, executeRaw, previewFrame, startPolling, stopPolling, polling } = useModbusChannel('rtu', 'rtu');
        const { form, options, parsedValues, validationErrors, isValid } = useRequestForm();
        const isPolling = ref(false);
        const previewHex = computed(() => previewFrame(options.value));
        onMounted(async () => {
            if (!support.value.supported)
                return;
            // 复用用户此前已授权的串口，省去每次重新选择
            try {
                const granted = await navigator.serial.getPorts();
                if (granted.length > 0) {
                    selectedPort.value = granted[0];
                    portName.value = describePort(granted[0], 0) + '（已授权）';
                }
            }
            catch {
                /* 忽略：部分环境下 getPorts 可能不可用 */
            }
        });
        async function pickPort() {
            const check = checkSerialSupport();
            support.value = check;
            if (!check.supported) {
                ElMessage.error(`${check.reason}。${check.solution}`);
                return;
            }
            try {
                const port = await navigator.serial.requestPort();
                selectedPort.value = port;
                portName.value = describePort(port);
                ElMessage.success('串口已选择');
            }
            catch (err) {
                // 用户点了取消不算错误，静默处理
                const msg = err instanceof Error ? err.message : String(err);
                if (!/No port selected|cancelled/i.test(msg)) {
                    ElMessage.error(`选择串口失败：${msg}`);
                }
            }
        }
        async function toggleConnection() {
            if (connected.value) {
                isPolling.value = false;
                await disconnect();
                ElMessage.info('串口已断开');
                return;
            }
            if (!selectedPort.value) {
                ElMessage.warning('请先选择一个串口');
                return;
            }
            try {
                await connect(new SerialTransport(selectedPort.value, { ...serial }));
                ElMessage.success(`串口已打开（${serial.baudRate} bps）`);
            }
            catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                ElMessageBox.alert(msg, '打开串口失败', { type: 'error', confirmButtonText: '知道了' });
            }
        }
        async function handleSend() {
            await execute(options.value, form.timeoutMs);
        }
        function handleTogglePoll() {
            if (isPolling.value) {
                stopPolling();
                isPolling.value = false;
            }
            else {
                startPolling(() => options.value, form.pollIntervalMs, form.timeoutMs);
                isPolling.value = true;
            }
        }
        async function handleSendRaw() {
            try {
                const bytes = hexToBytes(form.rawHex);
                if (bytes.length === 0) {
                    ElMessage.warning('请输入要发送的报文');
                    return;
                }
                await executeRaw(bytes, options.value.unitId, form.rawMode, form.timeoutMs);
            }
            catch (err) {
                ElMessage.error(err instanceof Error ? err.message : '报文格式错误');
            }
        }
        // polling 由 composable 内部维护，这里只做界面状态同步
        void polling;
        return (_ctx, _cache) => {
            const _component_router_link = _resolveComponent("router-link");
            const _component_el_alert = _resolveComponent("el-alert");
            const _component_el_input = _resolveComponent("el-input");
            const _component_el_button = _resolveComponent("el-button");
            const _component_el_form_item = _resolveComponent("el-form-item");
            const _component_el_option = _resolveComponent("el-option");
            const _component_el_select = _resolveComponent("el-select");
            const _component_el_form = _resolveComponent("el-form");
            const _component_el_card = _resolveComponent("el-card");
            const _component_el_col = _resolveComponent("el-col");
            const _component_el_row = _resolveComponent("el-row");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _cache[5] || (_cache[5] = _createElementVNode("div", { class: "title-block" }, [
                        _createElementVNode("h2", { class: "mb-page-title" }, "Modbus RTU 在线调试工具"),
                        _createElementVNode("span", { class: "mb-page-desc" }, "串口通信 · 实时采集 · 报文解析 · 可视化分析")
                    ], -1)),
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("span", {
                            class: _normalizeClass(["conn-dot", { online: _unref(connected), connecting: _unref(connecting) }])
                        }, null, 2),
                        _createTextVNode(" " + _toDisplayString(_unref(connected) ? '已连接' : _unref(connecting) ? '连接中…' : '未连接'), 1)
                    ])
                ]),
                (!support.value.supported)
                    ? (_openBlock(), _createBlock(_component_el_alert, {
                        key: 0,
                        type: "warning",
                        "show-icon": "",
                        closable: false,
                        class: "mb-card"
                    }, {
                        title: _withCtx(() => [
                            _createTextVNode(_toDisplayString(support.value.reason), 1)
                        ]),
                        default: _withCtx(() => [
                            _createElementVNode("div", null, _toDisplayString(support.value.solution), 1),
                            _createElementVNode("div", _hoisted_4, [
                                _cache[7] || (_cache[7] = _createTextVNode(" 如果暂时没有串口设备，也可以先切到 ", -1)),
                                _createVNode(_component_router_link, { to: "/tcp" }, {
                                    default: _withCtx(() => _cache[6] || (_cache[6] = [
                                        _createTextVNode("Modbus TCP 页面", -1)
                                    ])),
                                    _: 1,
                                    __: [6]
                                }),
                                _cache[8] || (_cache[8] = _createTextVNode(" 使用内置模拟从站体验完整流程。 ", -1))
                            ])
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_component_el_row, { gutter: 16 }, {
                    default: _withCtx(() => [
                        _createVNode(_component_el_col, {
                            xs: 24,
                            sm: 24,
                            md: 10,
                            lg: 8
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_component_el_card, { class: "mb-card" }, {
                                    header: _withCtx(() => _cache[9] || (_cache[9] = [
                                        _createElementVNode("div", { class: "card-header" }, [
                                            _createElementVNode("span", { class: "card-title" }, "串口连接设置")
                                        ], -1)
                                    ])),
                                    default: _withCtx(() => [
                                        _createVNode(_component_el_form, {
                                            "label-width": "88px",
                                            "label-position": "left"
                                        }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_el_form_item, { label: "串口" }, {
                                                    default: _withCtx(() => [
                                                        _createElementVNode("div", _hoisted_5, [
                                                            _createVNode(_component_el_input, {
                                                                "model-value": portName.value,
                                                                readonly: "",
                                                                placeholder: "尚未选择串口",
                                                                class: "port-input"
                                                            }, null, 8, ["model-value"]),
                                                            _createVNode(_component_el_button, {
                                                                icon: _unref(Search),
                                                                disabled: !support.value.supported || _unref(connected),
                                                                onClick: pickPort
                                                            }, {
                                                                default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                                    _createTextVNode("选择", -1)
                                                                ])),
                                                                _: 1,
                                                                __: [10]
                                                            }, 8, ["icon", "disabled"])
                                                        ]),
                                                        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "field-hint" }, " 浏览器出于隐私限制不会暴露 COM 号，需手动授权选择设备 ", -1))
                                                    ]),
                                                    _: 1,
                                                    __: [11]
                                                }),
                                                _createVNode(_component_el_form_item, { label: "波特率" }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_el_select, {
                                                            modelValue: serial.baudRate,
                                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((serial.baudRate) = $event)),
                                                            disabled: _unref(connected),
                                                            style: { "width": "100%" },
                                                            filterable: "",
                                                            "allow-create": ""
                                                        }, {
                                                            default: _withCtx(() => [
                                                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(BAUD_RATES, (b) => {
                                                                    return _createVNode(_component_el_option, {
                                                                        key: b,
                                                                        label: String(b),
                                                                        value: b
                                                                    }, null, 8, ["label", "value"]);
                                                                }), 64))
                                                            ]),
                                                            _: 1
                                                        }, 8, ["modelValue", "disabled"])
                                                    ]),
                                                    _: 1
                                                }),
                                                _createVNode(_component_el_form_item, { label: "数据位" }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_el_select, {
                                                            modelValue: serial.dataBits,
                                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((serial.dataBits) = $event)),
                                                            disabled: _unref(connected),
                                                            style: { "width": "100%" }
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_el_option, {
                                                                    label: "8",
                                                                    value: 8
                                                                }),
                                                                _createVNode(_component_el_option, {
                                                                    label: "7",
                                                                    value: 7
                                                                })
                                                            ]),
                                                            _: 1
                                                        }, 8, ["modelValue", "disabled"])
                                                    ]),
                                                    _: 1
                                                }),
                                                _createVNode(_component_el_form_item, { label: "停止位" }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_el_select, {
                                                            modelValue: serial.stopBits,
                                                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((serial.stopBits) = $event)),
                                                            disabled: _unref(connected),
                                                            style: { "width": "100%" }
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_el_option, {
                                                                    label: "1",
                                                                    value: 1
                                                                }),
                                                                _createVNode(_component_el_option, {
                                                                    label: "2",
                                                                    value: 2
                                                                })
                                                            ]),
                                                            _: 1
                                                        }, 8, ["modelValue", "disabled"])
                                                    ]),
                                                    _: 1
                                                }),
                                                _createVNode(_component_el_form_item, { label: "校验位" }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_el_select, {
                                                            modelValue: serial.parity,
                                                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((serial.parity) = $event)),
                                                            disabled: _unref(connected),
                                                            style: { "width": "100%" }
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_el_option, {
                                                                    label: "无校验 (None)",
                                                                    value: "none"
                                                                }),
                                                                _createVNode(_component_el_option, {
                                                                    label: "奇校验 (Odd)",
                                                                    value: "odd"
                                                                }),
                                                                _createVNode(_component_el_option, {
                                                                    label: "偶校验 (Even)",
                                                                    value: "even"
                                                                })
                                                            ]),
                                                            _: 1
                                                        }, 8, ["modelValue", "disabled"])
                                                    ]),
                                                    _: 1
                                                }),
                                                _createVNode(_component_el_form_item, { label: "流控" }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_el_select, {
                                                            modelValue: serial.flowControl,
                                                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((serial.flowControl) = $event)),
                                                            disabled: _unref(connected),
                                                            style: { "width": "100%" }
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_el_option, {
                                                                    label: "无 (None)",
                                                                    value: "none"
                                                                }),
                                                                _createVNode(_component_el_option, {
                                                                    label: "硬件 (RTS/CTS)",
                                                                    value: "hardware"
                                                                })
                                                            ]),
                                                            _: 1
                                                        }, 8, ["modelValue", "disabled"])
                                                    ]),
                                                    _: 1
                                                }),
                                                _createVNode(_component_el_form_item, null, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_el_button, {
                                                            type: _unref(connected) ? 'danger' : 'primary',
                                                            icon: _unref(connected) ? _unref(SwitchButton) : _unref(Link),
                                                            loading: _unref(connecting),
                                                            disabled: !support.value.supported,
                                                            onClick: toggleConnection
                                                        }, {
                                                            default: _withCtx(() => [
                                                                _createTextVNode(_toDisplayString(_unref(connected) ? '断开串口' : '打开串口'), 1)
                                                            ]),
                                                            _: 1
                                                        }, 8, ["type", "icon", "loading", "disabled"])
                                                    ]),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }),
                                _createVNode(RequestForm, {
                                    form: _unref(form),
                                    "parsed-values": _unref(parsedValues),
                                    "validation-errors": _unref(validationErrors),
                                    "is-valid": _unref(isValid),
                                    "preview-hex": previewHex.value,
                                    connected: _unref(connected),
                                    polling: isPolling.value,
                                    framing: "rtu",
                                    onSend: handleSend,
                                    onTogglePoll: handleTogglePoll,
                                    onSendRaw: handleSendRaw
                                }, null, 8, ["form", "parsed-values", "validation-errors", "is-valid", "preview-hex", "connected", "polling"])
                            ]),
                            _: 1
                        }),
                        _createVNode(_component_el_col, {
                            xs: 24,
                            sm: 24,
                            md: 14,
                            lg: 16
                        }, {
                            default: _withCtx(() => [
                                _createVNode(DataLogPanel, {
                                    channel: "rtu",
                                    height: "380px"
                                }),
                                _createVNode(_component_el_row, { gutter: 16 }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_el_col, {
                                            xs: 24,
                                            lg: 15
                                        }, {
                                            default: _withCtx(() => [
                                                _createVNode(RegisterViewer, { channel: "rtu" })
                                            ]),
                                            _: 1
                                        }),
                                        _createVNode(_component_el_col, {
                                            xs: 24,
                                            lg: 9
                                        }, {
                                            default: _withCtx(() => [
                                                _createVNode(StatusPanel, { channel: "rtu" })
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                })
                            ]),
                            _: 1
                        })
                    ]),
                    _: 1
                })
            ]));
        };
    }
});
